import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    ArrowLeft, Building2, MapPin, Calendar, TrendingUp, Users,
    CheckCircle, Clock, Home, Layers, ChevronRight, Edit2, Plus,
    DollarSign, AlertTriangle, Wallet
} from 'lucide-react';
import { Button, CardStats, Card, ExportDropdown } from '@components/ui';
import { CardGridSkeleton } from '@components/skeleton';
import { useExportPDF } from '@hooks';
import { getConstructionById } from '@mocks/insaatlar';
import { getBlocksByConstructionId } from '@mocks/bloklar';
import BlokModal from './components/BlokModal';
import clsx from 'clsx';

export default function ProjeDetay() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [construction, setConstruction] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedBlock, setSelectedBlock] = useState(null);

    useEffect(() => {
        // Simüle edilmiş veri yükleme
        const timer = setTimeout(() => {
            const data = getConstructionById(id);
            if (data) {
                setConstruction(data);
                setBlocks(getBlocksByConstructionId(id));
            }
            setIsLoading(false);
        }, 800);
        return () => clearTimeout(timer);
    }, [id]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const handleEditBlock = (block) => {
        setSelectedBlock(block);
        setModalOpen(true);
    };

    const handleAddBlock = () => {
        setSelectedBlock(null);
        setModalOpen(true);
    };

    const handleSubmitBlock = (data) => {
        if (selectedBlock) {
            toast.success(`${data.name} bloğu başarıyla güncellendi`);
        } else {
            toast.success(`${data.name} bloğu başarıyla eklendi`);
        }
        setModalOpen(false);
    };

    // Export
    const reportRef = useRef(null);
    const { exportToPDF, isExporting } = useExportPDF();

    const handleExportPDF = () => {
        exportToPDF({
            elementRef: reportRef,
            fileName: `insaat_${construction?.name || 'detay'}`,
            title: `İnşaat Detayı - ${construction?.name || ''}`,
            orientation: 'portrait',
        });
    };

    if (isLoading) {
        return (
            <div className="p-4 md:p-6">
                <CardGridSkeleton variant="block" cardCount={4} columns={2} />
            </div>
        );
    }

    if (!construction) {
        return (
            <div className="p-4 md:p-6 text-center py-16">
                <Building2 size={64} className="mx-auto mb-4 text-text-muted opacity-40" />
                <h2 className="text-xl font-bold text-text-primary mb-2">İnşaat Bulunamadı</h2>
                <p className="text-text-muted mb-4">Aradığınız inşaat kayıtlarımızda yok.</p>
                <Button variant="primary" onClick={() => navigate('/insaatlar')}>
                    İnşaatlara Dön
                </Button>
            </div>
        );
    }

    const { summary, progress = 0, status } = construction;

    const statusConfig = {
        planning: { label: 'Planlama', color: 'warning', icon: Clock },
        active: { label: 'Devam Ediyor', color: 'success', icon: TrendingUp },
        completed: { label: 'Tamamlandı', color: 'primary', icon: CheckCircle },
    };
    const currentStatus = statusConfig[status] || statusConfig.active;
    const StatusIcon = currentStatus.icon;

    // Block Card Component
    const BlockCard = ({ block }) => {
        const occupancy = Math.round(((block.soldApartments + block.reservedApartments) / block.totalApartments) * 100);
        const soldPct = Math.round((block.soldApartments / block.totalApartments) * 100);
        const reservedPct = Math.round((block.reservedApartments / block.totalApartments) * 100);

        return (
            <div
                onClick={() => handleEditBlock(block)}
                className={clsx(
                    'group bg-bg-secondary rounded-xl border border-border-light',
                    'hover:border-primary/30 hover:shadow-lg transition-all duration-200',
                    'cursor-pointer overflow-hidden'
                )}
            >
                {/* Header */}
                <div className="p-4 pb-3 border-b border-border-light">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                <Layers size={20} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
                                    {block.name}
                                </h3>
                                <p className="text-xs text-text-muted">
                                    {block.totalFloors} Kat • {block.totalApartments} Daire
                                </p>
                            </div>
                        </div>
                        <Link
                            to={`/daireler?block=${block.id}`}
                            className="p-2 rounded-lg text-text-muted hover:text-primary hover:bg-primary/10 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            title="Daireleri Gör"
                        >
                            <ChevronRight size={18} />
                        </Link>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 bg-success/10 rounded-lg">
                            <p className="text-lg font-bold text-success">{block.soldApartments}</p>
                            <p className="text-2xs text-text-muted">Satılan</p>
                        </div>
                        <div className="p-2 bg-warning/10 rounded-lg">
                            <p className="text-lg font-bold text-warning">{block.reservedApartments}</p>
                            <p className="text-2xs text-text-muted">Rezerve</p>
                        </div>
                        <div className="p-2 bg-bg-tertiary rounded-lg">
                            <p className="text-lg font-bold text-text-secondary">{block.emptyApartments}</p>
                            <p className="text-2xs text-text-muted">Boş</p>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                        <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-text-muted">Doluluk</span>
                            <span className="font-semibold text-text-primary">{occupancy}%</span>
                        </div>
                        <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden flex">
                            <div className="h-full bg-success transition-all" style={{ width: `${soldPct}%` }} />
                            <div className="h-full bg-warning transition-all" style={{ width: `${reservedPct}%` }} />
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-2xs text-text-muted">
                            <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-success" />
                                Satılan
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-warning" />
                                Rezerve
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 md:p-6 space-y-6" ref={reportRef}>
            {/* Header with Back Button */}
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate('/insaatlar')}
                        className="flex items-center gap-2 text-text-secondary hover:text-primary transition-colors w-fit"
                    >
                        <ArrowLeft size={18} />
                        <span className="text-sm font-medium">İnşaatlara Dön</span>
                    </button>
                    <ExportDropdown
                        onExportPDF={handleExportPDF}
                        isExporting={isExporting}
                    />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className={clsx(
                            'w-14 h-14 rounded-xl flex items-center justify-center shrink-0',
                            'bg-gradient-to-br shadow-lg',
                            currentStatus.color === 'success' && 'from-success/20 to-success/5',
                            currentStatus.color === 'warning' && 'from-warning/20 to-warning/5',
                            currentStatus.color === 'primary' && 'from-primary/20 to-primary/5',
                        )}>
                            <Building2 size={28} className={clsx(
                                currentStatus.color === 'success' && 'text-success',
                                currentStatus.color === 'warning' && 'text-warning',
                                currentStatus.color === 'primary' && 'text-primary',
                            )} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-text-primary">{construction.name}</h1>
                            <p className="text-text-muted flex items-center gap-1.5 mt-1">
                                <MapPin size={14} />
                                {construction.district}, {construction.city}
                            </p>
                        </div>
                    </div>

                    {/* Status Badge */}
                    <div className={clsx(
                        'px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 shrink-0',
                        currentStatus.color === 'success' && 'bg-success/10 text-success',
                        currentStatus.color === 'warning' && 'bg-warning/10 text-warning',
                        currentStatus.color === 'primary' && 'bg-primary/10 text-primary',
                    )}>
                        <StatusIcon size={16} />
                        {currentStatus.label}
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <Card padding="lg">
                <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-text-secondary">Proje İlerlemesi</span>
                    <span className="text-lg font-bold text-text-primary">{progress}%</span>
                </div>
                <div className="h-3 bg-bg-tertiary rounded-full overflow-hidden">
                    <div
                        className={clsx(
                            'h-full rounded-full transition-all duration-500',
                            progress >= 75 ? 'bg-success' : progress >= 40 ? 'bg-primary' : 'bg-warning'
                        )}
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-text-muted">
                    <span>Başlangıç: {formatDate(construction.startDate)}</span>
                    <span>Bitiş: {formatDate(construction.estimatedEndDate)}</span>
                </div>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Daire"
                    value={summary?.totalApartments || 0}
                    icon={Home}
                    color="primary"
                />
                <CardStats
                    title="Satılan"
                    value={summary?.soldApartments || 0}
                    icon={CheckCircle}
                    color="success"
                />
                <CardStats
                    title="Rezerve"
                    value={summary?.reservedApartments || 0}
                    icon={Clock}
                    color="warning"
                />
                <CardStats
                    title="Boş"
                    value={summary?.emptyApartments || 0}
                    icon={Home}
                    color="tertiary"
                />
            </div>

            {/* Financial Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card padding="lg" className="bg-gradient-to-br from-success/5 to-transparent">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                            <DollarSign size={20} className="text-success" />
                        </div>
                        <div>
                            <p className="text-xs text-text-muted">Toplam Gelir</p>
                            <p className="text-xl font-bold text-success">{formatCurrency(summary?.totalIncome || 0)}</p>
                        </div>
                    </div>
                </Card>
                <Card padding="lg" className="bg-gradient-to-br from-secondary/5 to-transparent">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                            <Wallet size={20} className="text-secondary" />
                        </div>
                        <div>
                            <p className="text-xs text-text-muted">Harcanan</p>
                            <p className="text-xl font-bold text-secondary">{formatCurrency(summary?.totalExpense || 0)}</p>
                        </div>
                    </div>
                </Card>
                <Card padding="lg" className="bg-gradient-to-br from-primary/5 to-transparent">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <TrendingUp size={20} className="text-primary" />
                        </div>
                        <div>
                            <p className="text-xs text-text-muted">Net Bakiye</p>
                            <p className="text-xl font-bold text-primary">{formatCurrency(summary?.netBalance || 0)}</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Blocks Section */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
                        <Layers size={20} className="text-primary" />
                        Bloklar ({blocks.length})
                    </h2>
                    <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={handleAddBlock}>
                        Blok Ekle
                    </Button>
                </div>

                {blocks.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {blocks.map((block) => (
                            <BlockCard key={block.id} block={block} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-bg-secondary rounded-xl border border-border-light">
                        <Layers size={48} className="mx-auto mb-4 text-text-muted opacity-40" />
                        <p className="text-text-secondary">Bu inşaatta henüz blok tanımlanmamış.</p>
                        <Button variant="outline" className="mt-4" onClick={handleAddBlock}>
                            İlk Bloğu Ekle
                        </Button>
                    </div>
                )}
            </div>

            {/* Block Modal */}
            <BlokModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmitBlock}
                block={selectedBlock}
            />
        </div>
    );
}
